import React, { createContext, useContext, useState, Children, isValidElement } from 'react';

interface SelectContextType {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  items: Array<{ value: string; children: React.ReactNode }>;
}

const SelectContext = createContext<SelectContextType | undefined>(undefined);

export interface SelectProps {
  children: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  placeholder?: string;
}

// Helper function to extract SelectItem components and placeholder from children
const extractSelectData = (children: React.ReactNode): {
  items: Array<{ value: string; children: React.ReactNode }>,
  placeholder?: string
} => {
  const items: Array<{ value: string; children: React.ReactNode }> = [];
  let placeholder: string | undefined;

  const processChild = (child: React.ReactNode) => {
    if (isValidElement(child)) {
      if (child.type === SelectTrigger) {
        // Process SelectTrigger children to find SelectValue
        Children.forEach(child.props.children, (triggerChild) => {
          if (isValidElement(triggerChild) && triggerChild.type === SelectValue) {
            placeholder = triggerChild.props.placeholder;
          }
        });
      } else if (child.type === SelectContent) {
        // Process SelectContent children
        Children.forEach(child.props.children, processChild);
      } else if (child.type === SelectItem) {
        // Extract SelectItem props
        items.push({
          value: child.props.value,
          children: child.props.children
        });
      }
    }
  };

  Children.forEach(children, processChild);
  return { items, placeholder };
};

export const Select: React.FC<SelectProps> = ({
  children,
  value,
  onValueChange,
  defaultValue,
  placeholder
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue || '');

  const currentValue = value !== undefined ? value : internalValue;
  const handleValueChange = (newValue: string) => {
    if (onValueChange) {
      onValueChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  // Extract items and placeholder from children
  const { items, placeholder: extractedPlaceholder } = extractSelectData(children);
  const finalPlaceholder = placeholder || extractedPlaceholder;

  return (
    <SelectContext.Provider value={{
      value: currentValue,
      onValueChange: handleValueChange,
      placeholder: finalPlaceholder,
      items
    }}>
      {children}
    </SelectContext.Provider>
  );
};

export interface SelectContentProps {
  children: React.ReactNode;
}

export const SelectContent: React.FC<SelectContentProps> = ({ children }) => {
  // This component is just a wrapper - the actual rendering happens in SelectTrigger
  return null;
};

export interface SelectItemProps extends React.OptionHTMLAttributes<HTMLOptionElement> {
  children: React.ReactNode;
  value: string;
}

export const SelectItem: React.FC<SelectItemProps> = ({ children, value, ...props }) => {
  // This will be processed by Select component, not rendered directly
  return null;
};

export interface SelectTriggerProps {
  children?: React.ReactNode;
  className?: string;
}

export const SelectTrigger: React.FC<SelectTriggerProps> = ({ children, className = '' }) => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('SelectTrigger must be used within Select');
  }

  const { value, onValueChange, placeholder, items } = context;

  return (
    <select
      className={`input ${className}`}
      value={value || ''}
      onChange={(e) => onValueChange(e.target.value)}
    >
      {placeholder && (
        <option value="">
          {placeholder}
        </option>
      )}
      {items.map((item, index) => (
        <option key={item.value || index} value={item.value}>
          {item.children}
        </option>
      ))}
    </select>
  );
};

export interface SelectValueProps {
  placeholder?: string;
}

export const SelectValue: React.FC<SelectValueProps> = ({ placeholder }) => {
  // This component doesn't render anything - it's just for API compatibility
  return null;
};
